import {
  SHBox,
  SHTable,
  SHTableBody,
  SHTableContainer,
} from "@components/design-systems";
import { FeePortfolioDetailDTO } from "@models/platform-analysis/entities/portfolioDetails";
import { FeeSubProductDTO } from "@models/platform-analysis/entities/steps/fee";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { FeePortfolioDetailsTableCell } from "@pages/platform-analysis/_id/steps/summary/components/tables/fee-portfolio-details/components/table-cell";
import { FeePortfolioDetailsTableRow } from "@pages/platform-analysis/_id/steps/summary/components/tables/fee-portfolio-details/components/table-row";
import { FeePortfolioDetailsConstants } from "@pages/platform-analysis/_id/steps/summary/constants";
import {
  displayIntNumber,
  getPortfolioDetails,
  getStringFamilyMemberAccounts,
  isAllExpandEssentialSubProductsHidden,
} from "@pages/platform-analysis/util";
import { formatCurrencyWithFixedDecimals } from "@utils";
import { useMemo } from "react";

export interface FeePortfolioDetailsTableProps {
  data?: FeePortfolioDetailDTO | null;
  isShowHoldingsNumber?: boolean;
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  hasNoPortfolioLevel?: boolean;
  hiddenSubProductIds?: string[];
  selectedSubProducts?: FeeSubProductDTO[];
}

export const FeePortfolioDetailsTable = ({
  data,
  isShowHoldingsNumber,
  portfolioSetup = FeeCalculatorPortfolioLevel.No,
  hasNoPortfolioLevel = portfolioSetup === FeeCalculatorPortfolioLevel.No,
  hiddenSubProductIds = [],
  selectedSubProducts = [],
}: FeePortfolioDetailsTableProps) => {
  const {
    transactionsOutside,
    transactionsWithin,
    investments,
    northBalances,
    cfsBalances,
    expandBalances,
  } = getPortfolioDetails(data);

  const retailInsurancePolicyInvestments: string[] = useMemo(
    () =>
      investments.filter((investment) =>
        investment.match(
          FeePortfolioDetailsConstants.RetailInsurancePoliciesRegex,
        ),
      ) ?? [],
    [investments],
  );

  const showCfsBalances = useMemo(() => cfsBalances.length > 0, [cfsBalances]);
  const showNorthBalances = useMemo(
    () => northBalances.length > 0,
    [northBalances],
  );
  const showExpandBalances = useMemo(() => {
    const isAllExpandEssentialsHidden = isAllExpandEssentialSubProductsHidden({
      selectedSubProducts,
      hiddenSubProductIds,
    });
    return !isAllExpandEssentialsHidden && expandBalances.length > 0;
  }, [expandBalances, hiddenSubProductIds, selectedSubProducts]);

  return (
    <SHTableContainer>
      <SHTable>
        <SHTableBody>
          {data?.familyMembers?.map((familyMember, index) => {
            return (
              <FeePortfolioDetailsTableRow key={index}>
                <FeePortfolioDetailsTableCell isFirstRow>
                  {familyMember.name}
                </FeePortfolioDetailsTableCell>
                <FeePortfolioDetailsTableCell colSpan={6}>
                  {getStringFamilyMemberAccounts(
                    familyMember.familyMemberAccounts,
                  )}
                </FeePortfolioDetailsTableCell>
              </FeePortfolioDetailsTableRow>
            );
          })}

          {data?.totalPortfolioValue && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.TotalPortfolioValue}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {formatCurrencyWithFixedDecimals({
                  value: data?.totalPortfolioValue,
                })}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {showCfsBalances && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.BalanceInCFSEdgeMenus}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {cfsBalances.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {showExpandBalances && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.BalanceInExpandEssentialMenus}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {expandBalances.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {showNorthBalances && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.BalanceInNorthMenus}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {northBalances.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {hasNoPortfolioLevel && investments.length > 0 && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.InvestmentsAndOtherHoldings}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {investments.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {isShowHoldingsNumber && data?.totalDifferentInvestments && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.TotalDifferentInvestmentsHeld}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {displayIntNumber(data?.totalDifferentInvestments)}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {transactionsOutside.length > 0 && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                <SHBox component={"span"}>
                  <>
                    Transactions <u>outside</u> managed accounts (Next 12
                    months)
                  </>
                </SHBox>
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {transactionsOutside.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {transactionsWithin.length > 0 && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                <SHBox component={"span"}>
                  <>
                    Transactions <u>within</u> managed accounts (Next 12 months)
                  </>
                </SHBox>
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {transactionsWithin.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}

          {!hasNoPortfolioLevel && retailInsurancePolicyInvestments.length > 0 && (
            <FeePortfolioDetailsTableRow>
              <FeePortfolioDetailsTableCell isFirstRow>
                {FeePortfolioDetailsConstants.NumberOfRetailInsurancePolices}
              </FeePortfolioDetailsTableCell>
              <FeePortfolioDetailsTableCell colSpan={6}>
                {retailInsurancePolicyInvestments.join(", ")}
              </FeePortfolioDetailsTableCell>
            </FeePortfolioDetailsTableRow>
          )}
        </SHTableBody>
      </SHTable>
    </SHTableContainer>
  );
};
